/**
 * Onboarding intro clip (≈23 s, loops) drawn in SVG + CSS: our replacement for a bundled intro
 * video. Six chapters with captions: logo → teacher draws → child draws on paper → snap a photo →
 * journal + sparks → family celebration. A thin timeline shows progress like a video scrubber.
 */
import { useEffect, useState } from 'react';
import { useT, type TKey } from '../lib/i18n';
import { SparkGlyph } from '../components/icons';
import { Figure, type Stage } from '../art/Figure';
import { useArtStyle } from '../art/useArtStyle';
import { drawingById, useDrawingReady } from '../lib/content';

/**
 * The Sitting Cat lesson figure (art system) placed in clip coordinates: `x, y` top-left, `size` px.
 * `build` steps through the 5 construction stages (teacher demo); otherwise a fixed stage.
 */
function CatArt({ x, y, size, stage = 5, build }: { x: number; y: number; size: number; stage?: Stage; build?: boolean }) {
  const styleId = useArtStyle();
  const [st, setSt] = useState<Stage>(build ? 1 : stage);
  useEffect(() => {
    if (!build) return;
    const id = setInterval(() => setSt((v) => (v >= 5 ? 5 : ((v + 1) as Stage))), 750);
    return () => clearInterval(id);
  }, [build]);
  const ready = useDrawingReady(drawingById('animals-cat'));
  const def = ready ? drawingById('animals-cat')?.art : undefined;
  if (!def) return null;
  return (
    <g transform={`translate(${x} ${y}) scale(${size / 240})`}>
      <Figure def={def} styleId={styleId} stage={st} animate={build} style={{ width: 240, height: 240 }} />
    </g>
  );
}

/** Chapter lengths (ms); total 23 000 ms. */
export const INTRO_CHAPTERS_MS = [3500, 4000, 4000, 3500, 4000, 4000] as const;
export const INTRO_TOTAL_MS = INTRO_CHAPTERS_MS.reduce((a, b) => a + b, 0);

function Scene({ n }: { n: number }) {
  switch (n) {
    case 0:
      return (
        <g className="ic-s0">
          <path className="ic-stroke ic-logo-star" d="M160 40l14 30 33 4-24 23 6 33-29-16-29 16 6-33-24-23 33-4z" fill="none" stroke="#FFC23A" strokeWidth="6" strokeLinejoin="round" pathLength={1} />
          <g className="ic-logo-pencil">
            <rect x="0" y="-5" width="44" height="10" rx="2" fill="#FFC23A" stroke="#132630" strokeWidth="2" />
            <path d="M0 -5l-10 5 10 5z" fill="#F6EFE3" stroke="#132630" strokeWidth="2" />
          </g>
          <text className="ic-logo-word" x="160" y="162" textAnchor="middle">
            Sketchling
          </text>
        </g>
      );
    case 1:
      return (
        <g className="ic-s1">
          <rect x="70" y="26" width="180" height="140" rx="14" fill="#15394A" stroke="#FFFBF4" strokeWidth="4" />
          <rect x="82" y="38" width="156" height="116" rx="6" fill="#FFFBF4" />
          <CatArt x={108} y={42} size={108} build />
          <g className="ic-step-dots">
            {[0, 1, 2, 3].map((i) => (
              <circle key={i} cx={136 + i * 16} cy="182" r="5" style={{ animationDelay: `${300 + i * 800}ms` }} />
            ))}
          </g>
          <circle className="ic-play" cx="232" cy="146" r="12" fill="#FF7A1A" />
          <path d="M228 140l10 6-10 6z" fill="#FFFBF4" />
        </g>
      );
    case 2:
      return (
        <g className="ic-s2">
          <rect x="62" y="22" width="196" height="150" rx="6" fill="#FFFBF4" stroke="#D8D2C8" strokeWidth="3" transform="rotate(-3 160 97)" />
          <CatArt x={100} y={34} size={120} build />
          <g className="ic-kid-pencil">
            <rect x="0" y="-6" width="56" height="12" rx="2" fill="#5AD1FF" stroke="#132630" strokeWidth="2.5" />
            <path d="M0 -6l-12 6 12 6z" fill="#F6EFE3" stroke="#132630" strokeWidth="2.5" />
          </g>
        </g>
      );
    case 3:
      return (
        <g className="ic-s3">
          <rect x="62" y="22" width="196" height="150" rx="6" fill="#FFFBF4" stroke="#D8D2C8" strokeWidth="3" transform="rotate(-3 160 97)" />
          <CatArt x={100} y={34} size={120} stage={4} />
          <g className="ic-phone">
            <rect x="96" y="30" width="128" height="140" rx="16" fill="none" stroke="#132630" strokeWidth="8" />
            <path d="M110 50v-8h8M202 42h8v8M210 150v8h-8M118 158h-8v-8" stroke="#FF7A1A" strokeWidth="4" fill="none" strokeLinecap="round" />
          </g>
          <rect className="ic-flash" x="0" y="0" width="320" height="200" fill="#FFFBF4" />
        </g>
      );
    case 4:
      return (
        <g className="ic-s4">
          <rect x="40" y="30" width="240" height="150" rx="10" fill="#8C5A3C" />
          <rect x="50" y="38" width="108" height="134" rx="4" fill="#F6EFE3" />
          <rect x="162" y="38" width="108" height="134" rx="4" fill="#FFFBF4" />
          <g className="ic-polaroid">
            <rect x="178" y="50" width="76" height="92" rx="3" fill="#FFFBF4" stroke="#D8D2C8" strokeWidth="2" />
            <CatArt x={180} y={52} size={72} />
          </g>
          <g className="ic-heart">
            <path d="M104 96c-10-8-16-4-16 2 0 8 16 16 16 16s16-8 16-16c0-6-6-10-16-2z" fill="#FF5C7A" />
          </g>
          {[0, 1, 2, 3, 4].map((i) => (
            <g key={i} className="ic-spark-fly" style={{ animationDelay: `${1300 + i * 120}ms` }}>
              <path d="M216 96l4 9 9 4-9 4-4 9-4-9-9-4 9-4z" fill="#FFC23A" />
            </g>
          ))}
          <g className="ic-counter">
            <rect x="248" y="6" width="64" height="24" rx="12" fill="#15394A" />
            <text x="290" y="23" textAnchor="middle">
              +10
            </text>
          </g>
        </g>
      );
    default:
      return (
        <g className="ic-s5">
          {Array.from({ length: 24 }, (_, i) => (
            <rect key={i} className="ic-confetti" x={(i * 41) % 320} y="-10" width="6" height="10" rx="1" fill={['#FF7A1A', '#FFC23A', '#5AD1FF', '#3DDC84', '#B98BFF', '#FF5C7A'][i % 6]} style={{ animationDelay: `${(i * 97) % 1200}ms` }} />
          ))}
          {[
            ['#FFB27A', 110, 0],
            ['#B98BFF', 160, 140],
            ['#5AD1FF', 210, 280],
          ].map(([c, x, d]) => (
            <g key={String(c)} className="ic-fam" style={{ animationDelay: `${d}ms` }}>
              <circle cx={x as number} cy="112" r="22" fill={c as string} stroke="#132630" strokeWidth="3" />
              <path d={`M${(x as number) - 8} 116q8 8 16 0`} stroke="#132630" strokeWidth="3" fill="none" strokeLinecap="round" />
              <circle cx={(x as number) - 7} cy="106" r="2.5" fill="#132630" />
              <circle cx={(x as number) + 7} cy="106" r="2.5" fill="#132630" />
              <path d={`M${(x as number) - 26} 170c2-20 12-30 26-30s24 10 26 30z`} fill={c as string} stroke="#132630" strokeWidth="3" />
            </g>
          ))}
          <g className="ic-trophy">
            <rect x="136" y="18" width="48" height="36" rx="10" fill="#FFFBF4" stroke="#132630" strokeWidth="3" />
            <CatArt x={142} y={18} size={36} />
          </g>
        </g>
      );
  }
}

export function IntroClip({ paused = false }: { paused?: boolean }) {
  const t = useT();
  const [n, setN] = useState(0);
  const [loop, setLoop] = useState(0);
  useEffect(() => {
    if (paused) return;
    const id = setTimeout(() => {
      if (n + 1 >= INTRO_CHAPTERS_MS.length) {
        setN(0);
        setLoop((l) => l + 1);
      } else setN(n + 1);
    }, INTRO_CHAPTERS_MS[n]);
    return () => clearTimeout(id);
  }, [n, paused]);
  const before = INTRO_CHAPTERS_MS.slice(0, n).reduce((a, b) => a + b, 0);
  return (
    <figure className="intro-clip" data-testid="intro-clip" data-chapter={n} aria-label={t('ast.intro.label')}>
      <svg key={`${loop}:${n}`} className="ic-stage" viewBox="0 0 320 200" role="img" aria-label={t(`ast.intro.${n + 1}` as TKey)}>
        <rect width="320" height="200" fill="#0D2A36" />
        <g className="ic-bg-stars">
          {[30, 90, 250, 290, 60, 200].map((x, i) => (
            <path key={i} d={`M${x} ${20 + ((i * 37) % 160)}l2 5 5 2-5 2-2 5-2-5-5-2 5-2z`} fill="#FFFBF4" opacity="0.3" style={{ animationDelay: `${i * 300}ms` }} />
          ))}
        </g>
        <Scene n={n} />
      </svg>
      <figcaption key={`c${loop}:${n}`} className="ic-caption">
        {n === 4 && <SparkGlyph size={18} />} {t(`ast.intro.${n + 1}` as TKey)}
      </figcaption>
      <div className="ic-timeline" aria-hidden>
        <i key={`${loop}:${n}`} style={{ ['--from' as string]: before / INTRO_TOTAL_MS, ['--to' as string]: (before + INTRO_CHAPTERS_MS[n]!) / INTRO_TOTAL_MS, animationDuration: `${INTRO_CHAPTERS_MS[n]}ms` }} />
      </div>
    </figure>
  );
}
